<!-- eslint-disable vue/prop-name-casing -->
<script setup>
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import { isEmpty, isNil, keyBy } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { useCommonImports } from '~/common/composables/common-imports.composable';
import { useTagsStore } from '~/common/stores/tags.store.js';
import { csvInjectionProtector, getUserFullName, sleep } from '~/common/utils/common.utils.js';
import { CONVERSION_RATES } from '~/project-management/constants/pm-constants.js';
import { useProjectManagementStore } from '~/project-management/store/pm.store.js';
import { generateUID } from '~/project-management/utils/pm-helper.utils';

const emit = defineEmits(['close']);

const { auth_store, common_store, $t, $toast, route } = useCommonImports();
const tags_store = useTagsStore();
const project_management_store = useProjectManagementStore();

const { $g, active_schedule, is_schedule_editable, show_overall_weights } = storeToRefs(project_management_store);

const {
  modify_config,
  preprocess_activity,
  update_active_schedule,
  is_critical_activity,
  update_bulk_activities_backend,
} = project_management_store;

const state = reactive({
  is_importing: false,
  is_exporting: false,
});

async function createCategoryIfNotExists(category_name) {
  if (!category_name)
    return null;

  const existing_category = common_store.categories.find(category => category.name === category_name);
  if (existing_category)
    return existing_category.uid;

  try {
    const { data } = await common_store.update_data({
      type: 'add',
      data: {
        category: {
          name: category_name,
          asset: route?.params?.asset_id,
          organization: auth_store.current_organization?.uid,
          parent: null,
        },
      },
      service: 'categories',
      append_data: false,
      response_key: 'category',
      state_prop: 'categories_map',
      update_state: false,
    });

    if (data?.category) {
      await common_store.update_global_data({ categories: [data.category.uid] });
      return data.category.uid;
    }
  }
  catch (error) {
    logger.error('Failed to create category:', error);
  }
  return null;
}

async function createTagIfNotExists(tag_name) {
  if (!tag_name)
    return null;

  const existing_tag = tags_store.tags.find(tag => tag.name === tag_name);
  if (existing_tag)
    return existing_tag.uid;

  try {
    const tag = await tags_store.add_tag(tag_name);
    if (tag)
      return tag.uid;
  }
  catch (error) {
    logger.error('Failed to create tag:', error);
  }
  return null;
}

async function downloadTemplate() {
  if (state.is_exporting)
    return;
  state.is_exporting = true;

  await sleep(500); // https://uxmag.com/articles/let-your-users-wait

  const ExcelJS = await import('exceljs');
  const { saveAs } = await import('file-saver');
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Schedule');

  const is_published = active_schedule.value.is_published;

  worksheet.properties.outlineProperties = { summaryBelow: false };

  const column_configs = getColumnConfigs();
  worksheet.columns = column_configs.map(({ header, key, width }) => ({ header, key, width }));
  const data = [];
  const links = $g.value.getLinks();

  const task_type_map = {
    [$g.value.config.types.project]: $t('WBS'),
    [$g.value.config.types.wbs]: $t('WBS'),
    [$g.value.config.types.task]: $t('Task'),
    [$g.value.config.types.milestone]: $t('Milestone'),
  };

  const priority_map = {
    1: $t('Critical'),
    2: $t('High'),
    3: $t('Medium'),
    4: $t('Low'),
  };

  let max_level = 0;
  $g.value.eachTask((task) => {
    if (task.type === $g.value.config.types.surrogate)
      return;

    const task_predecessors = links.filter(link => link.target === task.id);
    const predecessors = task_predecessors
      .map(link => `${csvInjectionProtector(link.source)} ${link.type}${link.lag ? `${link.lag < 0 ? '-' : '+'}${Math.abs(link.lag)} days` : ''}`)
      .join(', ');

    const accountable = Object.keys(task.accountable ?? {}).map((uid) => {
      if (common_store.is_type_user(uid))
        return getUserFullName(common_store.get_user(uid));
      else if (common_store.is_type_team(uid))
        return common_store.get_team(uid)?.name;
      return '';
    }).join(', ');

    const row_data = {
      wbs: $g.value.getWBSCode(task),
      id: csvInjectionProtector(task.id),
      activity: csvInjectionProtector(task.text),
      type: task_type_map[task.type],
      planned_start: dayjs(task.start_date).format('DD MMMM YYYY'),
      duration: task.duration,
      planned_finish: dayjs(task.end_date).format('DD MMMM YYYY'),
      actual_start: task.actual_start ? dayjs(task.actual_start).format('DD MMMM YYYY') : '',
      actual_finish: task.actual_finish ? dayjs(task.actual_finish).format('DD MMMM YYYY') : '',
      actual_duration: task.actual_duration,
      remaining_duration: task.remaining_duration,
      progress: Math.round(task.progress * 100),
      predecessors,
      critical: is_critical_activity(task) ? $t('Yes') : $t('No'),
      ...(active_schedule.value.has_activity_weightages ? show_overall_weights.value ? { overall_weight: task.overall_weight } : { weight: task.weight } : {}),
      priority: task.priority !== 5 ? priority_map[task.priority] : '',
      category: common_store.get_category(task.category)?.name,
      tags: task.tags?.map?.(tag => tags_store.get_tag(tag)?.name)?.join(', '),
      ...Object.entries(active_schedule.value.custom_fields).reduce((acc, [field_name, field]) => {
        if (field.type === 'date')
          acc[`custom_field_${field_name}`] = task.custom_field_values?.[field_name] ? dayjs(task.custom_field_values?.[field_name]).format('DD MMMM YYYY') : '';
        else
          acc[`custom_field_${field_name}`] = task.custom_field_values?.[field_name];
        return acc;
      }, {}),
      planned_cost: task.planned_cost,
      direct_planned_cost: task.direct_planned_cost,
      actual_cost: task.actual_cost,
      planned_work: task.planned_work,
      actual_work: task.actual_work,
      accountable,
      // Store auto progress sync status and type for conditional cell styling
      _has_auto_progress: task.auto_progress_sync?.is_enabled || false,
      _task_type: task.type,
      _has_predecessors: task_predecessors.length > 0,
      _task_level: $g.value.calculateTaskLevel(task),
    };
    max_level = Math.max(max_level, row_data._task_level);
    data.push(row_data);
  });
  worksheet.addRows(data.map(({ _has_auto_progress, _task_type, _has_predecessors, _task_level, ...row }) => row));

  const header_row = worksheet.getRow(1);
  column_configs.forEach((config, index) => {
    const cell = header_row.getCell(index + 1);
    cell.font = {
      bold: true,
    };

    const is_editable = is_published ? config.editable_published : config.editable_unpublished;

    if (is_editable === false) {
      cell.note = $t('This column is read-only and the values in this column will not be considered.');
    }
    else if (['actual_start', 'actual_finish', 'progress', 'overall_weight'].includes(config.key)) {
      cell.note = $t('Values are considered only for non-gray cells.');
    }
    else if (['direct_planned_cost'].includes(config.key)) {
      cell.note = $t('Values will be considered only for tasks');
    }
    else if (config.key === 'planned_work') {
      cell.note = $t('Values will be considered only for tasks if the progress is not configured to sync automatically');
    }
  });

  const row_count = worksheet.rowCount;
  for (let row_index = 2; row_index <= row_count; row_index++) {
    const row = worksheet.getRow(row_index);
    const row_data = data[row_index - 2];
    const has_auto_progress = row_data?._has_auto_progress || false;
    const task_type = row_data?._task_type;
    const is_task = task_type === $g.value.config.types.task;
    const is_milestone = task_type === $g.value.config.types.milestone;
    const has_predecessors = row_data?._has_predecessors || false;
    const task_level = row_data?._task_level || 0;

    if (max_level < 8)
      row.outlineLevel = task_level;

    column_configs.forEach((config, col_index) => {
      const cell = row.getCell(col_index + 1);
      let is_editable = is_published ? config.editable_published : config.editable_unpublished;

      if (
        ['actual_start', 'actual_finish', 'progress'].includes(config.key)
        && (
          has_auto_progress
          || (!is_task && !(is_milestone && !has_predecessors))
        )
      ) {
        is_editable = false;
      }

      if (['direct_planned_cost'].includes(config.key) && !is_task) {
        is_editable = false;
      }

      if (config.key === 'planned_work' && (!is_task || has_auto_progress)) {
        is_editable = false;
      }

      if (config.key === 'overall_weight' && !is_task && !is_milestone) {
        is_editable = false;
      }

      if (!is_editable || is_editable === false) {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD5D8DC' },
        };
      }
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `template - ${active_schedule.value.name}.xlsx`);
  state.is_exporting = false;
}

function getType({ duration, wbs }, all_wbs_codes) {
  if (wbs.split('.').length === 1)
    return $g.value.config.types.project;
  if (all_wbs_codes.includes(`${wbs}.1`))
    return $g.value.config.types.wbs;
  if (duration === 0)
    return $g.value.config.types.milestone;
  return $g.value.config.types.task;
}

function getColumnConfigs() {
  return [
    { header: $t('WBS'), key: 'wbs', width: 10, editable_published: false, editable_unpublished: true },
    { header: $t('ID'), key: 'id', width: 10, editable_published: false, editable_unpublished: true },
    { header: $t('Activity'), key: 'activity', width: 30, editable_published: false, editable_unpublished: true },
    { header: $t('Type'), key: 'type', width: 10, editable_published: false, editable_unpublished: false },
    { header: $t('Planned Start'), key: 'planned_start', width: 15, editable_published: false, editable_unpublished: true },
    { header: $t('Duration'), key: 'duration', width: 10, editable_published: false, editable_unpublished: true },
    { header: $t('Planned Finish'), key: 'planned_finish', width: 15, editable_published: false, editable_unpublished: false },
    { header: $t('Actual Start'), key: 'actual_start', width: 15, editable_published: 'conditional', editable_unpublished: false },
    { header: $t('Actual Finish'), key: 'actual_finish', width: 15, editable_published: 'conditional', editable_unpublished: false },
    { header: $t('Actual Duration'), key: 'actual_duration', width: 10, editable_published: false, editable_unpublished: false },
    { header: $t('Remaining Duration'), key: 'remaining_duration', width: 10, editable_published: false, editable_unpublished: false },
    { header: `${$t('Progress')} %`, key: 'progress', width: 10, editable_published: 'conditional', editable_unpublished: false },
    ...(active_schedule.value.has_activity_weightages ? [{ header: $t('Weight'), key: show_overall_weights.value ? 'overall_weight' : 'weight', width: 10, editable_published: false, editable_unpublished: 'conditional' }] : []),
    { header: $t('Predecessors'), key: 'predecessors', width: 15, editable_published: false, editable_unpublished: true },
    { header: $t('Critical'), key: 'critical', width: 10, editable_published: false, editable_unpublished: false },
    { header: $t('Priority'), key: 'priority', width: 10, editable_published: true, editable_unpublished: true },
    { header: $t('Category'), key: 'category', width: 10, editable_published: true, editable_unpublished: true },
    { header: $t('Tags'), key: 'tags', width: 10, editable_published: true, editable_unpublished: true },
    ...Object.entries(active_schedule.value.custom_fields).map(([field_name]) => ({ header: field_name, key: `custom_field_${field_name}`, width: 10, editable_published: true, editable_unpublished: true })),
    { header: $t('Planned cost'), key: is_schedule_editable.value ? 'direct_planned_cost' : 'planned_cost', width: 10, editable_published: false, editable_unpublished: true },
    { header: $t('Actual cost'), key: 'actual_cost', width: 10, editable_published: false, editable_unpublished: false },
    { header: $t('Total work'), key: 'planned_work', width: 10, editable_published: true, editable_unpublished: true },
    { header: $t('Actual work'), key: 'actual_work', width: 10, editable_published: false, editable_unpublished: false },
    { header: $t('Accountable'), key: 'accountable', width: 10, editable_published: true, editable_unpublished: true },
  ];
}

function normalizeHeader(value) {
  if (value === undefined || value === null)
    return '';
  return value.toString().trim().replace(/\s+/g, ' ').toLowerCase();
}

function getParent(wbs, data) {
  if (!wbs || wbs.split('.').length <= 1)
    return $g.value.config.root_id;

  const parent_wbs = wbs.split('.').slice(0, -1).join('.');
  const parent_task = data.find(item => item.wbs === parent_wbs);
  return parent_task?.id || $g.value.config.root_id;
}

function detectMissingParents(all_wbs_codes) {
  const missing_parents = [];
  for (const wbs_code of all_wbs_codes) {
    const parent_wbs = wbs_code.split('.').slice(0, -1).join('.');
    if (parent_wbs && !all_wbs_codes.includes(parent_wbs))
      missing_parents.push(wbs_code);
  }
  return missing_parents;
}

function getLinkProperties(text, all_ids) {
  const link = {
    uid: generateUID(),
    type: 'FS',
    lag: 0,
    source: null,
  };

  const parts_of_text = text.split(' ');
  const predecessor_id = parts_of_text?.[0];
  if (!all_ids.includes(predecessor_id))
    return link; // the text does not contain any valid ID, so return with source set as null

  link.source = predecessor_id;

  if (parts_of_text.length > 1)
    text = parts_of_text.slice(1, parts_of_text.length).join('');
  else
    return link; // the text only contains the ID, so there's no point in parsing further

  if (text.match(/^(FS|SS|FF|SF)/i)) {
    link.type = (text.match(/^(FS|SS|FF|SF)/i)[0]).toUpperCase();
    text = text.replace(link.type, '').trim();
  }

  if (text.startsWith('+') || text.startsWith('-')) {
    const lag_value = text.match(/[-+]\d+/)[0];
    link.lag = Number.parseFloat(lag_value);
    text = text.replace(lag_value, '').trim();
  }
  link.lag = !link.lag || Number.isNaN(link.lag) ? 0 : link.lag;

  const pattern = /(days*|weeks*|months*|years*|[dwmy])?/g;
  if (text.match(pattern)) {
    const unit = text.match(pattern)[0];
    if (unit in CONVERSION_RATES)
      link.lag = link.lag * CONVERSION_RATES[unit];
  }
  link.lag = Math.ceil(link.lag);

  return link;
}

async function handlePublishedModeImport(xlsx_data, _all_ids, id_uid_map) {
  const updates = [];

  for (const activity of xlsx_data) {
    const activity_uid = id_uid_map[activity.id];
    if (!activity_uid) {
      handleError(`${$t('Activity with ID')} ${activity.id} ${$t('not found in schedule')}`);
      return false;
    }

    const existing_activity = active_schedule.value.activities[activity_uid];
    if (!existing_activity) {
      handleError(`${$t('Activity with ID')} ${activity.id} ${$t('not found in schedule')}`);
      return false;
    }

    const update_payload = {
      uid: activity_uid,
    };

    const has_auto_progress = existing_activity.auto_progress_sync?.is_enabled;
    const task_type = existing_activity.type;
    const is_task = task_type === $g.value.config.types.task;

    if (!has_auto_progress) {
      if (activity.actual_start)
        update_payload.actual_start = dayjs(activity.actual_start).utc(true).toISOString();
      if (activity.actual_finish)
        update_payload.actual_finish = dayjs(activity.actual_finish).utc(true).toISOString();
      if (!isNil(activity.progress) && activity.progress !== existing_activity.progress)
        update_payload.progress = activity.progress;
    }

    if (!isNil(activity.priority))
      update_payload.priority = activity.priority;
    if (activity.category)
      update_payload.category = activity.category;
    if (activity.tags?.length)
      update_payload.tags = activity.tags;
    // if (!isNil(activity.actual_cost))
    //   update_payload.actual_cost = activity.actual_cost;
    if (!isNil(activity.planned_work) && is_task && !has_auto_progress)
      update_payload.planned_work = activity.planned_work;

    // if (!isNil(activity.actual_work))
    //   update_payload.actual_work = activity.actual_work;
    if (!isEmpty(activity.accountable))
      update_payload.accountable = activity.accountable;

    if (activity.custom_field_values && Object.keys(activity.custom_field_values).length > 0) {
      update_payload.custom_field_values = {};
      for (const [field_name, field_value] of Object.entries(activity.custom_field_values)) {
        if (field_value !== undefined && field_value !== null && field_value !== '') {
          update_payload.custom_field_values[field_name] = field_value;
        }
      }
    }

    updates.push(update_payload);
  }

  await update_bulk_activities_backend(updates);
  return true;
}

async function handleUnpublishedModeImport(xlsx_data, all_ids, all_wbs_codes, id_uid_map, processed_data) {
  for (const [index, activity] of xlsx_data.entries()) {
    for (const predecessor of activity.predecessors) {
      const link = getLinkProperties(predecessor, all_ids);

      if (link.source === null) {
        handleError(`${$t('Invalid predecessor in row')} ${index + 2}`);
        return false;
      }

      if (link.source === activity.id) {
        handleError(`Self-referential predecessor in row ${index + 2}`);
        return false;
      }

      processed_data.links.push({
        ...link,
        target: activity.id,
      });
    }

    const existing_activity_data = active_schedule.value.activities[id_uid_map[activity.id]];
    if (existing_activity_data) {
      delete existing_activity_data.end_date;
      delete existing_activity_data.planned_finish;
    }

    const task_type = getType(activity, all_wbs_codes);
    const is_task = task_type === $g.value.config.types.task;
    const is_project = task_type === $g.value.config.types.project;

    const existing_project_uid = is_project
      ? Object.values(active_schedule.value.activities).find(a => a.is_root)?.uid
      : null;
    const activity_uid = id_uid_map[activity.id] || existing_project_uid || generateUID();

    processed_data.data.push(preprocess_activity({
      ...(existing_activity_data || {}),
      ...activity,
      type: task_type,
      uid: activity_uid,
      parent: getParent(activity.wbs, xlsx_data),
      ...(show_overall_weights.value
        ? { overall_weight: isNil(activity.overall_weight) || (task_type !== $g.value.config.types.task && task_type !== $g.value.config.types.milestone) ? null : activity.overall_weight / 100 }
        : { weight: isNil(activity.weight) ? null : activity.weight / 100 }
      ),
      priority: activity.priority,
      category: activity.category,
      tags: activity.tags,
      custom_field_values: activity.custom_field_values,
      direct_planned_cost: is_task ? activity.direct_planned_cost : null,
      // actual_cost: activity.actual_cost,
      planned_work: is_task ? activity.planned_work : null,
      // actual_work: activity.actual_work,
      // actual_start: activity.actual_start,
      // actual_finish: activity.actual_finish,
      // progress: activity.progress,
      accountable: activity.accountable,
      ...Object.keys(activity.custom_field_values).reduce((acc, key) => {
        acc[`custom_field_${key}`] = activity.custom_field_values[key];
        return acc;
      }, {}),
      is_backend_save_pending: id_uid_map[activity.id]
        ? !!existing_activity_data.is_backend_save_pending
        : true,
    }, false));
  }

  processed_data.links = processed_data.links.filter((link, index) => {
    return processed_data.links.findIndex(l => l.source === link.source && l.target === link.target) === index;
  });

  $g.value.clearAll();
  $g.value.parse(processed_data);
  active_schedule.value.activities = keyBy(processed_data.data, 'uid');
  active_schedule.value.relations = processed_data.links;

  modify_config({ key: 'wbs_level', value: Number.MAX_SAFE_INTEGER });
  modify_config({ key: 'wbs_level_max', value: Number.MAX_SAFE_INTEGER });

  $g.value.batchUpdate(() => {
    $g.value.eachTask((task) => {
      if (task.type === $g.value.config.types.project || task.type === $g.value.config.types.wbs) {
        task.direct_planned_cost = 0;
        if (show_overall_weights.value)
          task.overall_weight = 0;
      }

      const should_update_cost = task.type === $g.value.config.types.task && task.direct_planned_cost;
      const should_update_weight = show_overall_weights.value && [$g.value.config.types.task, $g.value.config.types.milestone].includes(task.type) && task.overall_weight;

      if (should_update_cost || should_update_weight) {
        $g.value.eachParent((parent) => {
          if (should_update_cost) {
            parent.direct_planned_cost = (parent.direct_planned_cost || 0) + task.direct_planned_cost;
          }
          if (should_update_weight) {
            parent.overall_weight = Number.parseFloat(((parent.overall_weight || 0) + task.overall_weight).toFixed(12));
          }
          $g.value.updateTask(parent.id);
        }, task.id);
      }
    });

    $g.value.eachTask((task) => {
      $g.value.open(task.id);
    });
  });
  $g.value.render();
  return true;
}

async function ingestSpreadsheetData(data) {
  state.is_importing = true;
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(data);
  const worksheet = workbook.worksheets[0];
  try {
    const headerRowValues = (worksheet.getRow(1).values || []).slice(1);
    const actualHeaders = headerRowValues.map(h => normalizeHeader(h));
    const expectedRaw = getColumnConfigs().map(c => c.header);
    const expectedHeaders = expectedRaw.map(h => normalizeHeader(h));

    const mismatches = [];
    for (let i = 0; i < expectedHeaders.length; i++) {
      const actual = actualHeaders[i] || '';
      if (actual !== expectedHeaders[i]) {
        const foundVal = worksheet.getRow(1).getCell(i + 1).value ?? '';
        mismatches.push(`${$t('expected')} "${expectedRaw[i]}", ${$t('found')} "${foundVal}"`);
      }
    }
    if (mismatches.length) {
      return handleError(`${$t('Invalid column names')} - ${mismatches.join('; ')}`);
    }
  }
  catch {
    return handleError($t('Invalid column names'));
  }
  const rows = worksheet.getSheetValues();

  const all_ids = [];
  const all_wbs_codes = [];
  const processed_data = {
    data: [],
    links: [],
  };

  const id_uid_map = {};
  $g.value.eachTask((task) => {
    if (task.type === $g.value.config.types.surrogate)
      return;
    id_uid_map[task.id] = task.uid;
  });

  const xlsx_data = [];
  const is_published = active_schedule.value.is_published;

  const useful_rows = rows.slice(2);

  if (!useful_rows.length) {
    return handleError($t('No data found'));
  }

  for (const [row_index, raw_row] of useful_rows.entries()) {
    try {
      if (!raw_row?.some(Boolean))
        continue;

      const row = raw_row.map((item) => {
        if (typeof item === 'object' && item?.result)
          return DOMPurify.sanitize(item?.result?.toString?.() ?? '', { ALLOWED_TAGS: [] });
        else
          return DOMPurify.sanitize(item?.toString?.() ?? '', { ALLOWED_TAGS: [] });
      });
      // NOTE: this row may still (and likely does) contain empty values as map skips them;
      // so don't remove the optional chaining before the row?.[x]?.function calls in the following code

      const extracted_wbs = row?.[1]?.trim();
      if (!/^\d+(?:\.\d+)*$/.test(extracted_wbs)) {
        return handleError(`${$t('Invalid WBS code in row')} ${row_index + 2}`);
      }

      const extracted_id = row?.[2]?.trim();
      if (!extracted_id) {
        return handleError(`${$t('Missing ID in row')} ${row_index + 2}`);
      }

      const extracted_text = row?.[3]?.trim();
      if (!extracted_text) {
        return handleError(`${$t('Missing activity name in row')} ${row_index + 2}`);
      }

      const extracted_planned_start = row?.[5]?.trim();
      const extracted_duration = Number.parseInt(row?.[6]);
      // const extracted_planned_finish = row?.[7]?.trim(); // Not used in parsing, only for display
      const extracted_actual_start = row?.[8]?.trim();
      const extracted_actual_finish = row?.[9]?.trim();
      // const extracted_actual_duration = Number.parseInt(row?.[10]); // Computed field, not editable
      // const extracted_remaining_duration = Number.parseInt(row?.[11]); // Computed field, not editable
      const extracted_progress = Number.parseFloat(row?.[12]);
      if ((!is_schedule_editable.value) && ((extracted_progress < 0 || extracted_progress > 100) || (extracted_duration === 0 && (extracted_progress !== 0 && extracted_progress !== 100)))) {
        return handleError(`${$t('Invalid progress in row')} ${row_index + 2}`);
      }

      // 'predecessors', 'weight', and other optional fields
      const has_weight = active_schedule.value.has_activity_weightages;
      const extracted_weight = has_weight ? Number.parseFloat(row?.[13]) : null;

      const predecessors_col = 13 + (has_weight ? 1 : 0);
      // const critical_col = 14 + (has_weight ? 1 : 0); // Not editable
      const priority_col = 15 + (has_weight ? 1 : 0);
      const category_col = 16 + (has_weight ? 1 : 0);
      const tags_col = 17 + (has_weight ? 1 : 0);
      const custom_fields_start_col = 18 + (has_weight ? 1 : 0);

      const extracted_predecessors = (row?.[predecessors_col]?.split(',') || []).map(item => item.trim());
      const extracted_priority = row?.[priority_col]?.trim();
      const extracted_category = row?.[category_col]?.trim();
      const extracted_tags = (row?.[tags_col]?.split(',') || []).map(item => item.trim());

      const custom_fields_count = Object.keys(active_schedule.value.custom_fields).length;
      const extracted_custom_fields = Object.entries(active_schedule.value.custom_fields).reduce((acc, [field_name, field], idx) => {
        acc[field_name] = row?.[custom_fields_start_col + idx]?.trim();
        if (!isNil(acc[field_name])) {
          if (field.type === 'date') {
            const dayjs_obj = dayjs(acc[field_name]);
            acc[field_name] = dayjs_obj.isValid() ? dayjs_obj.toISOString() : null;
          }
          else if (field.type === 'number' || field.type === 'money') {
            acc[field_name] = Number.parseFloat(acc[field_name]);
            if (Number.isNaN(acc[field_name]))
              acc[field_name] = null;
          }
        }
        return acc;
      }, {});

      const planned_cost_col = custom_fields_start_col + custom_fields_count;
      const actual_cost_col = planned_cost_col + 1;
      const planned_work_col = actual_cost_col + 1;
      const actual_work_col = planned_work_col + 1;

      const extracted_planned_cost = Number.parseFloat(row?.[planned_cost_col]);
      // const extracted_actual_cost = Number.parseFloat(row?.[actual_cost_col]);
      const extracted_planned_work = Number.parseFloat(row?.[planned_work_col]);
      // const extracted_actual_work = Number.parseFloat(row?.[actual_work_col]);

      const accountable_col = actual_work_col + 1;
      const extracted_accountable = (row?.[accountable_col]?.split(',') || []).map(item => item.trim());

      all_ids.push(extracted_id);
      all_wbs_codes.push(extracted_wbs);

      const priority_map_reverse = {
        [$t('Critical')]: 1,
        [$t('High')]: 2,
        [$t('Medium')]: 3,
        [$t('Low')]: 4,
      };

      let category_uid_from_name = null;
      if (extracted_category) {
        category_uid_from_name = await createCategoryIfNotExists(extracted_category);
      }

      const tag_uids_from_names = [];
      for (const tag_name of extracted_tags) {
        if (tag_name) {
          const tag_uid = await createTagIfNotExists(tag_name);
          if (tag_uid) {
            tag_uids_from_names.push(tag_uid);
          }
        }
      }

      const accountable_map = {};
      const existing_activity = active_schedule.value.activities[id_uid_map[extracted_id]];
      const existing_accountable = existing_activity?.accountable || {};
      for (const accountable_name of extracted_accountable) {
        const user = common_store.users.find(user => user.email === accountable_name || getUserFullName(user) === accountable_name);
        if (user) {
          accountable_map[user.uid] = existing_accountable[user.uid] ?? 'read';
          continue;
        }

        const team = common_store.teams.find(team => team.name === accountable_name);
        if (team) {
          accountable_map[team.uid] = existing_accountable[team.uid] ?? 'read';
          continue;
        }
      }

      xlsx_data.push({
        id: extracted_id,
        wbs: extracted_wbs,
        text: extracted_text,
        duration: extracted_duration,
        planned_duration: extracted_duration,
        planned_start: extracted_planned_start ? new Date(extracted_planned_start) : null,
        predecessors: extracted_predecessors.filter(Boolean),
        ...(show_overall_weights.value
          ? { overall_weight: Number.isNaN(extracted_weight) ? null : extracted_weight }
          : { weight: Number.isNaN(extracted_weight) ? null : extracted_weight }),
        priority: extracted_priority ? priority_map_reverse[extracted_priority] : 5,
        category: category_uid_from_name,
        tags: tag_uids_from_names,
        custom_field_values: extracted_custom_fields,
        ...(is_schedule_editable.value
          ? {
              direct_planned_cost: Number.isNaN(extracted_planned_cost) ? null : extracted_planned_cost,
            }
          : {
              actual_start: extracted_actual_start ? new Date(extracted_actual_start) : null,
              actual_finish: extracted_actual_finish ? new Date(extracted_actual_finish) : null,
              progress: Number.isNaN(extracted_progress) ? 0 : extracted_progress / 100,
            }),
        // actual_cost: Number.isNaN(extracted_actual_cost) ? null : extracted_actual_cost,
        planned_work: Number.isNaN(extracted_planned_work) ? null : extracted_planned_work,
        // actual_work: Number.isNaN(extracted_actual_work) ? null : extracted_actual_work,
        accountable: accountable_map,
        row_index: row_index + 2, // NOTE: Just for error reporting
      });
    }
    catch (error) {
      console.error(error);
      return handleError(`${$t('Error in row')} ${row_index + 2}`);
    }
  }

  for (const activity of xlsx_data) {
    const task_type = getType(activity, all_wbs_codes);

    if (task_type === $g.value.config.types.task || task_type === $g.value.config.types.milestone) {
      if (!activity.planned_start || !dayjs(activity.planned_start).isValid()) {
        return handleError(`${$t('Invalid planned start date in row')} ${activity.row_index}`);
      }

      if (Number.isNaN(activity.duration)) {
        return handleError(`${$t('Invalid duration in row')} ${activity.row_index}`);
      }
    }
  }

  const missing_parents = detectMissingParents(all_wbs_codes);
  if (missing_parents.length) {
    return handleError(`${$t('Missing parent activities')} ― ${missing_parents.join(', ')}`);
  }

  const root_level_tasks = xlsx_data.filter(task => !task.wbs.includes('.'));
  if (root_level_tasks.length > 1) {
    return handleError($t('Multiple project tasks found. Only one project task is allowed.'));
  }

  for (const activity of xlsx_data) {
    const activity_wbs_parts = activity.wbs.split('.');
    const activity_ancestors = [];

    let ancestor_wbs = '';
    for (const part of activity_wbs_parts.slice(0, -1)) {
      ancestor_wbs = ancestor_wbs ? `${ancestor_wbs}.${part}` : part;
      activity_ancestors.push(ancestor_wbs);
    }

    for (const predecessor of activity.predecessors) {
      const predecessor_task = xlsx_data.find(task => task.id === predecessor);
      if (predecessor_task && activity_ancestors.includes(predecessor_task.wbs)) {
        return handleError(`${activity.text} ${$t('cannot have its ancestor')} ${predecessor_task.text} ${$t('as a predecessor')}`);
      }
    }
  }

  const are_all_weights_null = xlsx_data.every(activity => activity.weight === null);

  if (!are_all_weights_null && !active_schedule.value.has_activity_weightages) {
    await update_active_schedule(
      active_schedule.value.uid,
      { has_activity_weightages: true },
      true,
    );
  }

  // if there are any duplicate IDs found, show an error and return
  if (all_ids.length !== new Set(all_ids).size) {
    return handleError($t('Duplicate IDs found'));
  }

  let is_import_successful = true;
  if (is_published) {
    // Published mode: prepare API payload
    is_import_successful = await handlePublishedModeImport(xlsx_data, all_ids, id_uid_map);
  }
  else {
    // Unpublished mode: parse and feed to gantt
    is_import_successful = await handleUnpublishedModeImport(xlsx_data, all_ids, all_wbs_codes, id_uid_map, processed_data);
  }

  if (!is_import_successful) {
    state.is_importing = false;
    return;
  }

  state.is_importing = false;
  $toast({
    title: $t('Success'),
    text: $t('XLSX file imported successfully'),
    type: 'success',
    timeout: 4000,
  });
  emit('close');
}

function submitHandler(form$) {
  try {
    const file = form$.data.imported_file;
    if (!file?.name?.endsWith('.xlsx')) {
      return handleError($t('Unsupported file'));
    }
    const reader = new FileReader();
    reader.onload = e => ingestSpreadsheetData(e.target.result);
    reader.readAsArrayBuffer(file);
  }
  catch (err) {
    emit('close');
    logger.error(err);
    handleError($t('Failed to import your data'));
  }
}

function handleError(message) {
  $toast({
    title: $t('Error'),
    text: message,
    type: 'error',
    timeout: 4000,
  });
  logger.error(message);
  state.is_importing = false;
}
</script>

<template>
  <HawkModalContainer content_class="w-64 rounded-lg">
    <Vueform
      :display-errors="false"
      size="sm"
      :should_validate_on_mount="false"
      @submit="submitHandler"
    >
      <div class="col-span-12">
        <HawkModalHeader @close="emit('close')">
          <template #left>
            {{ $t('Import') }}
          </template>
        </HawkModalHeader>
        <HawkModalContent class="max-h-96 scrollbar">
          <StaticElement name="static">
            <div class="text-gray-600">
              {{ $t("Download a pre-formatted XLSX file to use as a template for your import.") }}
            </div>
            <HawkButton :loading="state.is_exporting" type="outlined" class="text-gray-600 font-semibold mt-3" @click="downloadTemplate">
              <IconHawkDownloadOne />
              {{ $t('Download template') }}
            </HawkButton>
            <div class="border-t border-t-gray-200 my-4" />
          </StaticElement>
          <FileElement
            name="imported_file"
            :presets="['hawk_file_element']"
            :options="{
              clickable_text: $t('Click to import'),
              text: $t('or drag and drop'),
            }"
            accept=".xlsx"
            :use_uppy="false"
            :drop="true"
            :auto="false"
            :submit="false"
            :clickable="false"
            :url="false"
            :rules="['required']"
            :messages="{ required: 'This field is required.' }"
          />
          <div class="text-xs text-warning-700 mt-3 flex items-center">
            <IconHawkAlertTriangle class="w-3 h-3 mr-1" />
            {{ $t('Importing will overwrite your existing data') }}
          </div>
        </HawkModalContent>
        <HawkModalFooter class="flex justify-between items-center">
          <template #right>
            <div class="flex justify-end">
              <HawkButton type="outlined" class="mr-3 font-semibold" @click="emit('close')">
                {{ $t('Cancel') }}
              </HawkButton>
              <ButtonElement
                submits
                size="sm"
                name="submit"
                :button-label="$t('Import')"
                :loading="state.is_importing"
              />
            </div>
          </template>
        </HawkModalFooter>
      </div>
    </Vueform>
  </HawkModalContainer>
</template>
